import { MenuItem, Select } from "@mui/material";
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import styles from "./index.module.scss";

interface AppOption {
  appid: number;
  appname: string;
}

interface TableComponentProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  updateData: (rowIndex: number, columnId: keyof TData, value: string | number) => void;
  isEditable: boolean;
  appOptions?: AppOption[];
}

const selectSx = {
  fontSize: 14,
  "& .MuiSelect-select": { padding: "8px 12px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "2px solid",
    borderColor: "var(--border-accent, #1976d2)",
  },
  backgroundColor: "transparent",
};

const RequiredFunctionTable = <TData extends object>({
  data,
  columns,
  updateData,
  isEditable,
  appOptions = [],
}: TableComponentProps<TData>) => {
  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
  });

  // 既存データの「標準機能」を「基本機能」に表示マッピング
  const normalizeImplementationMethod = (value: string): string =>
    value === "標準機能" ? "基本機能" : value;

  const isUrl = (value: string) => {
    try {
      new URL(value);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <table className={styles["table"]}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} style={{ cursor: "default" }}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {cell.column.id === "delete" ? (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                ) : cell.column.id === "appid" && !isEditable ? (
                  <div className={styles["readonly-cell"]}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ) : cell.column.id === "appid" && isEditable ? (
                  <Select
                    value={cell.getValue() as number}
                    onChange={(e) =>
                      updateData(row.index, cell.column.id as keyof TData, Number(e.target.value))
                    }
                    fullWidth
                    size="small"
                    sx={selectSx}
                  >
                    <MenuItem value={0}>選択してください</MenuItem>
                    {appOptions.map((app) => (
                      <MenuItem key={app.appid} value={app.appid}>
                        {app.appname}
                      </MenuItem>
                    ))}
                  </Select>
                ) : !isEditable && isUrl(cell.getValue() as string) ? (
                  <a
                    className={styles["link"]}
                    href={cell.getValue() as string}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cell.getValue() as string}
                  </a>
                ) : cell.column.id === "how" && isEditable ? (
                  <Select
                    value={normalizeImplementationMethod(cell.getValue() as string)}
                    onChange={(e) =>
                      updateData(row.index, cell.column.id as keyof TData, e.target.value as string)
                    }
                    fullWidth
                    size="small"
                    sx={selectSx}
                  >
                    <MenuItem value="">選択してください</MenuItem>
                    <MenuItem value="基本機能">基本機能</MenuItem>
                    <MenuItem value="プラグイン">プラグイン</MenuItem>
                    <MenuItem value="カスタマイズ">カスタマイズ</MenuItem>
                  </Select>
                ) : (
                  <textarea
                    value={
                      cell.column.id === "how"
                        ? normalizeImplementationMethod(cell.getValue() as string)
                        : (cell.getValue() as string | number)
                    }
                    onChange={(e) =>
                      updateData(row.index, cell.column.id as keyof TData, e.target.value)
                    }
                    readOnly={!isEditable}
                    style={{ width: "100%", resize: "none" }}
                  />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default RequiredFunctionTable;
